// The page's one Web Audio context (issues #3718, #3747): created on first use, resumed from the
// first gesture by whoever plays first, and then left running for as long as the page lives. It is
// never closed (WebKit plays quietly for the rest of the process once a page lets its audio go fully
// idle before capturing again, docs/learn/audio-research.md, 2026-09-04) and never suspended on a
// hide (on the iPhone, with a session type declared, suspending it while hidden and resuming it on
// the return left the page's output half a second late for the rest of the app's life, 2026-10-02).
// The Player's Web Audio path and the Learn audio session share it, so the page audio session's
// rules (client/src/audio/audio_session.ts) apply to one context, and the soundfont plays on it.
//
// It is created by whoever plays first, inside that gesture and after the page audio session has
// been declared, which is where the measurements were made; nothing creates it at startup (the
// soundfont decodes offline, client/src/player/instrument.ts). Both players start it through
// resumePageAudioContext(), synchronously inside the tap that starts them, which is where iOS
// requires the first resume.
//
// The frozen clock (issue #4098). On iOS 27 a return to the foreground after a few seconds in
// another app can leave the context reporting 'running' with its clock stopped: `currentTime` does
// not move, no statechange fires, and a resume() of the running context changes nothing, so a song
// scheduled on it never sounds (iPhone 12 mini, iOS 27.0.1, 2026-10-27; open WebKit bugs 263627,
// 276016, 281566 and 291892 describe the same). A suspend and then a resume of that context brought
// the clock back from where it stood, with or without a gesture; `outputLatency` and `baseLatency`
// read the same before and after, and the song played on time. Why the suspend of 2026-10-02 left
// the output late and these did not is not known; one hypothesis is that it suspended a context
// that was still rendering, which a frozen one is not. So the clock is watched: on every return to
// the foreground, and whenever the context comes back to 'running', a sample of `currentTime` is
// taken and checked again a moment later; a visible page whose running clock has not moved is healed
// with a suspend and a resume, retried a few times should it not take. The tap that plays checks too
// (resumePageAudioContext), as the backstop for a freeze the watchdog has not caught yet, and
// schedules the same timed check when it cannot tell yet, so a freeze no foreground event announced
// is healed a moment after the tap rather than at the next one. A hidden page is never checked, and
// a clock that moves is never touched. A heal restarts the clock without the players asking, so
// whoever maps another clock onto it (the Learn session) hears of each restart through
// subscribeAudioClockRestarts(), once per restart however many resumes report it. The watchdog never
// waits on the browser for ever: a suspend or a resume it started from its own timer, outside any
// gesture, may never settle, so it lets go of each after browserCallTimeoutMs, and a tap that
// arrives meanwhile resumes the context itself.
//
// A dead output (issue #4116). The context can also report 'running' with a frozen clock that the
// quick heal above, a suspend followed at once by a resume, does not restart: on the iPhone (iOS
// 27.0.1, 2026-10-28, after 14 minutes in the background with a paused song) every such heal settled
// in a few ms and left the clock frozen, and so did a silent media element played inside a gesture,
// paused and replayed, and a change of the audio session type; suspending the contexts, holding them
// suspended for a second and resuming them (resumes of about 70 ms) moved the clock on from where it
// stood, and only then did the OS log WebKit's output unit stopping and starting. The reading made
// then, that the unit's stop is asynchronous and a suspend held long enough lets it land, is wrong
// for the case measured (2026-10-28, later the same day): WebKit renders all of a page's contexts
// through one output unit, which stops only once none of them runs, and the app kept a second one
// running that nobody suspended, the context the soundfont was decoded on, so a suspend of this
// context alone, held or not, never stopped the unit and its resume had nothing to restart; the held
// suspend that worked had suspended every context. So every heal suspends, together with this
// context, every other context of the app's that runs at its start (live_audio_contexts.ts), and
// resumes them with it; one that does not run then is someone else's to start and is left alone.
// Only this context's suspend gates the heal: another context's that is refused, or that has not
// settled within otherSuspendTimeoutMs, fails nothing, and the heal goes on without it. Measured
// with the soundfont's decode context closed once it had decoded (2026-10-28, 18:08-18:12), the
// page's context was the only one left, and the quick heal alone restarted the dead output, in two
// runs of two (its resume took about 100 ms, the real start of the unit, against 6-20 ms for the
// resumes that had restarted nothing). What shipped goes further: the soundfont and the vocals
// decode on an OfflineAudioContext, which has no output (decodeAudioFile() in
// client/src/player/instrument.ts), so no decode context runs at all, and the registry holds only
// the page's context and any other real-time context made to take part in heals. The quick heal
// therefore stays the first attempt, being the cheaper one, the one that heals a freeze whose output
// unit still runs (2026-10-27), and now the one that restarts a dead output on a page with one
// context. The held heal is kept as the second attempt, as a decision: it was part of the one
// procedure measured to work with a second context still running (every context suspended, held a
// second, resumed), and it costs a second only once a quick heal has failed. So the sequence on a
// dead output is: a quick heal; the check restartCheckDelayMs after its resume finds the clock still
// frozen, logs that the heal did not take, and heals again at once with the suspend held for
// heldSuspendMs; the check after that finds the clock moving.
//
// Once a heal is known not to have taken (until the clock is seen moving or the page is hidden),
// every heal is held, the play tap's included, so the song sounds about a second after the tap; the
// tap heals then on any clock that has not moved since its sample, however young, where it
// otherwise needs a sample frozenSampleMinAgeMs old (a tap 85 ms after one made a plain resume and
// no sound on the iPhone, 2026-10-28). Except while the check that judges a heal is still pending,
// up to restartCheckDelayMs after its resume settled, and would heal again should it find the clock
// frozen: a restarted context can take a few hundred ms to render its first quantum, so a clock that
// has not moved since then says nothing yet, and the tap makes the plain resume and leaves the
// judgement to that check (on a dead output its heal follows within the second; on a working one the
// tap sounds at once). Once the watchdog's heals have reached the cap, that check heals nothing, so
// the tap heals as it would with no check pending. A tap during a hold waits on
// it rather than cutting it short with a resume of its own (on a visible page: a tap on a hidden one
// makes the plain resume, below). Only a heal that reached its resume and saw it settle, on a
// context whose clock the watchdog has seen move at least once, can be found not to have taken: one
// whose suspend was refused or never landed, or whose context left 'running' before the check, says
// nothing about the output, and nor does any heal on a clock never seen moving, which on a platform
// with no output device (a desktop without one, a headless run) never moves at all; there every
// heal stays quick, at a cost of milliseconds. The cap counts held heals like quick ones, and only
// the watchdog's own: after it the context is left to the play tap, which is unbounded by design,
// each tap healing once.
// A check that finds the clock moving after a heal that did not take, with no restart announced
// since, announces one, since the output may have come back by itself; that announcement comes when
// the check runs, up to restartCheckDelayMs after the clock started moving, where a heal's own
// announcement comes the moment its resume settles.
//
// A heal the watchdog let go of (see browserCallTimeoutMs: its suspend or its resume did not settle
// in time) still sees the call it was waiting on settle whenever the browser gets to it, and by then
// a newer heal may be under way; each heal is numbered, and a chain that was let go of, or that
// finds a newer one started, logs it and makes no further call and no announcement of a restart it
// did not make (a resume released late with no newer heal started did restart the clock, and
// announces it, unless the watchdog's own resume of the context announced it first). A browser call
// it made before it learnt that can still land, though: a stale suspend that lands after the newer
// heal resumed (or after the heal was let go of) leaves the context suspended, so the chain, unless
// a newer heal is under way (which resumes the context, and the other contexts, itself), hands it to
// the watchdog's own resume (see resumeOwnSuspend); the other contexts are not the stale chain's to
// resume, the watchdog having resumed them when it let go of it, and another context's suspend that
// lands after its heal went on without it goes to the heal under way, or is resumed at once with
// none (see suspendOthers); and a stale resume the browser releases late (a tap's activation
// releasing one iOS held back) can resume the context inside a newer heal's hold: a hold cut short
// that way that did not take is found by the check a second later, which heals again (within the
// cap).
//
// Every event that changes the watchdog's course (a foreground signal that schedules a check, a
// statechange to or away from 'running', a check's outcome, a heal with its kind and timings, a heal
// that did not take, the cap once it is reached, the play tap's decision) is logged as one
// console.log line prefixed `[audio-clock]`, with the context's state and clock
// (client/src/audio/audio_clock_log.ts): recordConsoleLog() (client/src/util/logging.ts) keeps the
// last 300 console.log lines in localStorage (LocalStorageKey.Logging), so on a device they are the
// record of what the clock did before someone reported a silent play button. That record is short,
// and recordConsoleLog() rewrites all of it on every line, inside the gesture when the line comes
// from a tap, so the healthy path is kept quiet: a play tap on a moving clock, and the check it
// schedules when that check finds the clock moving, log nothing (the Player logs its play and pause,
// one line each, and the Now Playing element only what goes wrong with it). On iOS one return to the
// foreground delivers three signals and a statechange per change of state, so a signal that keeps
// the pending check, and a statechange between two states other than 'running', log nothing too;
// nothing is logged on a cadence of its own, and the checks that keep sampling after the cap stay
// quiet.
//
// The pieces that touch the browser's page (whether the document is visible, and the page's
// foreground signal from client/src/util/page_foreground.ts, focus included) are behind
// AudioClockWatchdogOptions, so the unit tests run the real watchdog under jsdom and fake timers.

import { sleep } from '../../../common/src/sleep.ts';
import { createAudioContext } from '../player/instrument.ts';
import { errorNameAndMessage } from '../util/describe_error.ts';
import { subscribePageForeground } from '../util/page_foreground.ts';
import { logAudioClock } from './audio_clock_log.ts';
import { liveAudioContexts } from './live_audio_contexts.ts';

// How long after a sample the clock must have moved: a running clock advances by a render quantum
// (under 3 ms) at a time, so this is a hundred quanta and still too short for anyone to notice
const clockCheckDelayMs = 300;
// The same, for a clock that has just started or restarted, whether by the watchdog's heal, a tap's
// resume or the browser ending an interruption: such a context can take a few hundred ms to render
// its first quantum, and suspending it then, just as it starts rendering, is what the header's
// hypothesis for the late output of 2026-10-02 says to avoid
const restartCheckDelayMs = 1000;
// The play tap's check needs a sample at least this old: younger, a healthy clock may not have
// rendered the next quantum yet. Not on an output a heal has already failed to restart, where a
// frozen sample of any age will do, unless a heal's own check is still to judge it (see the header).
const frozenSampleMinAgeMs = 100;
// How long a heal that follows a heal that did not take holds the context suspended before it
// resumes it (issue #4116): on the iPhone (iOS 27.0.1, 2026-10-28) a running context frozen at
// 868.509 s survived every back-to-back suspend and resume (1 ms + 6 ms, 1 ms + 15 ms), and moved on
// at once from where it stood when every context was suspended, held for this long and resumed
// (resumes of 77 and 70 ms); only then did the OS log WebKit's output unit stopping and starting.
// What made the difference was suspending every context, not the hold: with the app's only other
// context gone, the quick heal alone restarted the same dead output (see the header). The hold is
// kept for a second context still running, where it is what was measured to work; whether less
// would do there was not measured.
const heldSuspendMs = 1000;
// Heals the watchdog tries in a row, with no check finding the clock moving in between, before it
// leaves the context to the next tap
const maxConsecutiveHeals = 3;
// Heals that log a warning per page: on a platform with no working output device a running clock
// never moves, and every tap there heals harmlessly (quickly: a clock never seen moving never makes
// a heal held, see clockSeenMoving), so later heals stay quiet
const maxHealWarnings = 3;
// How long the watchdog waits on each browser call of a heal (its suspend, its resume), or on a
// resume of a context it suspended, before it lets go: Chrome and iOS Safari can leave a resume()
// made without a user activation pending for ever rather than reject it
// (client/src/pages/learn/range/range_finder_page.tsx), and the watchdog's own calls come from a
// timer
const browserCallTimeoutMs = 2000;
// How long a heal waits on the suspends of the app's other contexts before it goes on without the
// ones still pending (issue #4116): only this context's suspend gates the heal, the others are
// suspended so the shared output unit can stop, and iOS and Chrome can leave a call made outside a
// gesture pending for ever. On the iPhone a suspend settles in a few ms (2026-10-28).
const otherSuspendTimeoutMs = 250;

// The part of `document` that says whether the page is visible, and when that changes; the
// watchdog reads whether it is visible, and the Learn session listens to it as well
export interface PageVisibility {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
}

// Whether the page is visible, without the change events: all the watchdog reads
type PageVisibilityState = Pick<PageVisibility, 'visibilityState'>;

export interface AudioClockWatchdogOptions {
  // whether the page is visible; the default is `document`
  document?: PageVisibilityState;
  // the page's returns to the foreground and departures from it, subscribed with focus as one more
  // return (free and idempotent here); the default is the app's
  subscribePageForeground?: typeof subscribePageForeground;
}

function elapsedMs(since: number): number {
  return Math.round(performance.now() - since);
}

interface ClockSample {
  time: number; // the context's currentTime, in seconds
  at: number; // when it was read, performance.now() in ms
}

// The one deadline the watchdog puts on a browser call (see browserCallTimeoutMs): calls `missed`
// should `call` not settle within browserCallTimeoutMs, and returns `call` itself, which is left to
// settle whenever it does. A heal uses it as it is, to let go of itself (see heal()); everything
// else through withDeadline.
function onDeadline<T>(call: Promise<T>, missed: () => void): Promise<T> {
  const timer = setTimeout(missed, browserCallTimeoutMs);
  const clear = () => clearTimeout(timer);
  call.then(clear, clear);
  return call;
}

function deadlineError(what: string): Error {
  return new Error(`${what} did not settle within ${browserCallTimeoutMs} ms (issue #4098)`);
}

// `call`, settling as it does unless the deadline comes first (see onDeadline), in which case it
// rejects with deadlineError(what)
function withDeadline<T>(call: Promise<T>, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    void onDeadline(call, () => reject(deadlineError(what)));
    call.then(resolve, reject);
  });
}

class AudioClockWatchdog {
  private readonly document: PageVisibilityState;
  private readonly restartListeners = new Set<() => void>();
  private latest: ClockSample | undefined; // what the play tap's check compares with
  private timer: ReturnType<typeof setTimeout> | undefined;
  private dueAt = 0; // when the pending check runs, performance.now() in ms
  // The pending check was scheduled by a play tap on a clock that moves, or by a page's first tap,
  // and replaced no check that logs: routine, so it logs nothing should it find the clock moving and
  // owe nothing (see the header on the record)
  private pendingCheckQuiet = false;
  // By the watchdog, since the page was last hidden or its clock seen moving
  private consecutiveHeals = 0;
  private warnedHeals = 0;
  private healing = false; // until the heal settles, or its timeout after it started
  // Heals started on this context; a heal's chain is the current one while this is still its number
  private healsStarted = 0;
  // The heal under way while it holds the context suspended (see heldSuspendMs), for a tap to wait on
  private holdingHeal: Promise<void> | undefined;
  // Set before a heal's suspend and cleared once the context runs again: a heal whose resume was
  // refused leaves the context suspended, which only the watchdog knows to undo
  private suspendedByWatchdog = false;
  // Set with suspendedByWatchdog and cleared by the next announcement of a restart: however many
  // resumes report a suspend undone, the listeners hear of it once (see announceRestartOnce)
  private restartOwed = false;
  private resumingOwnSuspend = false;
  // The app's other contexts a heal suspended along with this one (see live_audio_contexts.ts) that
  // no resume has restored yet, each added as its suspend lands while a heal is under way (or is
  // refused, for a context that does not close): the heal under way resumes them with its own
  // resume, or, should it fail first, once it settles
  private readonly suspendedOthers = new Set<AudioContext>();
  // Set when a heal's own chain sees its resume settle, and cleared by the next check that runs (the
  // one that finds whether it took), by a hide, which cancels that check, and by the context leaving
  // 'running': a heal that failed before it resumed, or a context that stopped after it, says
  // nothing about whether a resume restarts the output
  private nextCheckJudgesHeal = false;
  // Heals in a row that a check found had not taken, the play tap's included, since the page was
  // last hidden or its clock seen moving; above zero, the output is taken for dead and every heal
  // holds its suspend (see heldSuspendMs). Only counted once clockSeenMoving is set.
  private healsWithoutEffect = 0;
  // A check or a tap has seen this context's clock move at least once. Until then a heal that leaves
  // the clock frozen says nothing about the output: on a platform with no output device (a desktop
  // without one, a headless run) the clock never moves at all, and holding every heal there would
  // cost a second per tap and three per return for nothing, so heals stay quick.
  private clockSeenMoving = false;
  // A check found a heal had not taken, and since then no restart has been announced, no check has
  // found the clock moving and the page has not been hidden: a clock found moving then has come back
  // with no heal announcing it (the output restarted by itself), so that check announces it. Not
  // healsWithoutEffect > 0 with restarted() zeroing that count: the count must outlive a restart, so
  // a heal made before the next check (a play tap's, or the one after a refused resume) still holds
  private restartUnannounced = false;

  constructor(
    private readonly context: AudioContext,
    options: AudioClockWatchdogOptions,
  ) {
    this.document = options.document ?? document;
    (options.subscribePageForeground ?? subscribePageForeground)(
      (foreground) => {
        if (!foreground) {
          this.log('hidden signal');
          this.noteHidden();
          return;
        }
        if (this.scheduleCheck()) {
          this.log(`foreground signal, check scheduled in ${clockCheckDelayMs} ms`);
        }
      },
      { focus: true },
    );
    let previousState = context.state;
    context.addEventListener('statechange', () => {
      const wasRunning = previousState === 'running';
      previousState = context.state;
      if (context.state !== 'running') {
        if (wasRunning) {
          this.log('statechange away from running');
        }
        this.nextCheckJudgesHeal = false;
        return;
      }
      if (!wasRunning) {
        this.log('statechange to running');
      }
      this.suspendedByWatchdog = false;
      this.scheduleCheck(restartCheckDelayMs);
    });
  }

  // The play tap's check (see resumePageAudioContext): a resume made now when a heal is under way, a
  // wait on a heal that holds the context suspended, a heal started now for a running clock that has
  // not moved since a sample old enough to tell, or undefined when the clock is not known to be
  // frozen. Once a heal has been found not to take (the output is taken for dead), a clock that has
  // not moved since its sample, however young, is healed, with the hold (see heal()): on the iPhone
  // (2026-10-28) a tap 85 ms after the check's sample on such an output made a plain resume and no
  // sound. While a heal's own check is still to judge it, the tap leaves it to that check and makes
  // the plain resume, unless the watchdog's heals have reached the cap and that check would heal
  // nothing (see the header). When it cannot tell, the clock is sampled again and checked a
  // moment later, so a freeze that began without a return to the foreground is healed shortly after
  // this tap. A hidden page (a play from the lock screen's
  // controls) is never healed and never waits on a heal: it gets undefined, and so the plain resume
  // the tap made before the watchdog existed, inside the gesture. That resume cancels nothing: a held
  // heal under way resumes a running context when its hold ends, which does nothing, and its check,
  // once the page is visible, decides from the clock.
  healIfFrozen(): Promise<void> | undefined {
    if (this.pageIsHidden()) {
      return undefined;
    }
    if (this.holdingHeal) {
      // A resume now would cut the hold short, and with it the heal (see heldSuspendMs): the tap
      // sounds once the heal resumes the context, about a second after it began
      this.log('tap: waiting on a held heal');
      return this.holdingHeal;
    }
    if (this.healing) {
      // A heal the watchdog started from its timer may be stuck on a resume the browser holds back
      // for a user activation (see browserCallTimeoutMs). This tap is one, so the context is resumed
      // here, synchronously inside it, which is what can succeed, and the tap waits on that rather
      // than on the heal. A resume of a context that is already running does nothing; the heal's own
      // chain still settles by itself if it ever does, and the watchdog lets go of it in any case.
      this.log('tap: resuming through a pending heal');
      return this.context.resume().then(() => this.announceRestartOnce());
    }
    if (!this.canCheck()) {
      return undefined;
    }
    const sample = this.latest;
    if (!sample) {
      // The first check of this context: on a desktop it is made inside this tap already 'running',
      // so no statechange announced its start, and a clock that has only just started may not have
      // rendered its first quantum yet
      this.log(`tap: no sample yet, check scheduled in ${restartCheckDelayMs} ms`);
      this.scheduleCheck(restartCheckDelayMs, { quiet: true });
      return undefined;
    }
    if (this.clockMovedSince(sample)) {
      // The healthy tap: nothing to log (see the header on the record)
      this.clockSeenMoving = true;
      this.scheduleCheck(clockCheckDelayMs, { quiet: true });
      return undefined;
    }
    if (this.nextCheckJudgesHeal && this.consecutiveHeals < maxConsecutiveHeals) {
      // The heal's resume settled less than restartCheckDelayMs ago, and its clock may not have
      // rendered its first quantum yet: the check due then judges it, and heals again should it not
      // have taken. At the cap it would heal nothing, so the tap goes on to heal itself.
      this.log(`tap: a heal's check is pending in ${Math.round(this.dueAt - performance.now())} ms, plain resume`);
      return undefined;
    }
    const outputDead = this.healsWithoutEffect > 0;
    if (outputDead || performance.now() - sample.at >= frozenSampleMinAgeMs) {
      const onDead = outputDead ? ', on an output a heal has failed to restart' : '';
      this.log(`tap: healing inside the tap, clock frozen since a sample ${elapsedMs(sample.at)} ms old${onDead}`);
      return this.heal('the play tap');
    }
    this.log(`tap: sample ${elapsedMs(sample.at)} ms old, too young to tell, the pending check stands`);
    return undefined;
  }

  // resumePageAudioContext() for a watched context (see there)
  resumeForTap(): Promise<boolean> {
    const healing = this.healIfFrozen();
    if (healing) {
      return healing.then(() => true);
    }
    // A resume of a context that runs on a visible page does nothing and is the healthy tap's:
    // unlogged. A start, or a play from the lock screen, is one line once it settles.
    const state = this.context.state;
    const hidden = this.pageIsHidden();
    const startedAt = performance.now();
    return resumeReportingStart(this.context).then(
      (started) => {
        if (state !== 'running' || hidden) {
          const of = `of a ${state} context${hidden ? ' on a hidden page' : ''}`;
          this.log(`tap: plain resume ${of} settled in ${elapsedMs(startedAt)} ms`);
        }
        return started;
      },
      (error: unknown) => {
        this.log(`tap: plain resume refused after ${elapsedMs(startedAt)} ms: ${errorNameAndMessage(error)}`);
        throw error;
      },
    );
  }

  // Called after every restart of the clock this watchdog made, and once when a check finds the
  // clock moving after a heal that did not take, until the returned function is called (see
  // subscribeAudioClockRestarts for how late each comes)
  subscribeRestarts(listener: () => void): () => void {
    this.restartListeners.add(listener);
    return () => this.restartListeners.delete(listener);
  }

  private log(event: string) {
    logAudioClock('watchdog', event, this.context);
  }

  private pageIsHidden(): boolean {
    return this.document.visibilityState === 'hidden';
  }

  // Whether a check may look at the clock at all: never while a heal is under way, on a hidden page
  // or on a context that is not running
  private canCheck(): boolean {
    return !this.healing && !this.pageIsHidden() && this.context.state === 'running';
  }

  private clockMovedSince(sample: ClockSample): boolean {
    return this.context.currentTime !== sample.time;
  }

  private noteHidden() {
    this.cancelCheck();
    this.consecutiveHeals = 0;
    this.healsWithoutEffect = 0;
    this.nextCheckJudgesHeal = false; // the check that would have followed the heal is cancelled
    this.restartUnannounced = false; // a moving clock after the return owes no announcement
  }

  // A sample now, and a check of it after `delayMs`; a later scheduled check replaces both, unless
  // the check already pending is due no sooner than the one asked for: on iOS a return from a long
  // absence brings the context back to 'running' (a check after restartCheckDelayMs) and then the
  // foreground signal's burst (focus, visibilitychange and the native signal) within milliseconds,
  // none of which may cut that grace short. A context the watchdog suspended and could not resume is
  // resumed instead. A quiet check logs nothing should it find the clock moving and owe nothing
  // (see the header on the record). Returns whether a new check was scheduled.
  private scheduleCheck(delayMs = clockCheckDelayMs, { quiet = false } = {}): boolean {
    if (this.pageIsHidden()) {
      this.cancelCheck();
      // A heal that ends on a hidden page is followed by no check: the first check after the return
      // follows no heal of that foreground period
      this.nextCheckJudgesHeal = false;
      return false;
    }
    if (this.healing) {
      this.cancelCheck();
      return false;
    }
    if (this.suspendedByWatchdog && this.context.state === 'suspended') {
      this.cancelCheck();
      this.resumeOwnSuspend();
      return false;
    }
    const dueAt = performance.now() + delayMs;
    if (this.timer !== undefined && this.dueAt >= dueAt) {
      return false; // the pending check keeps its sample
    }
    // A quiet check that replaces one that logs (a tap's, behind a return's) logs as that one would
    const checkQuiet = quiet && (this.timer === undefined || this.pendingCheckQuiet);
    this.cancelCheck();
    const sample = this.takeSample();
    this.latest = sample;
    this.dueAt = dueAt;
    this.pendingCheckQuiet = checkQuiet;
    this.timer = setTimeout(() => this.check(sample, delayMs, checkQuiet), delayMs);
    return true;
  }

  private cancelCheck() {
    if (this.timer === undefined) {
      return;
    }
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  private check(sample: ClockSample, delayMs: number, quiet: boolean) {
    this.timer = undefined;
    if (!this.canCheck()) {
      return;
    }
    const followsHeal = this.nextCheckJudgesHeal;
    this.nextCheckJudgesHeal = false;
    const outcome = `sample ${elapsedMs(sample.at)} ms old, delay ${delayMs} ms`;
    if (this.clockMovedSince(sample)) {
      if (!quiet || followsHeal || this.consecutiveHeals > 0 || this.healsWithoutEffect > 0) {
        this.log(`check: moved since ${sample.time.toFixed(3)}, ${outcome}`);
      }
      this.clockSeenMoving = true;
      this.consecutiveHeals = 0; // the clock moves: the context is healthy again
      this.healsWithoutEffect = 0;
      if (this.restartUnannounced) {
        this.restarted();
      }
      return;
    }
    // The heal did not take, and on a clock that has moved before that means a dead output: the
    // heal this check makes, at once, holds its suspend (see heldSuspendMs). On a clock never seen
    // moving it means nothing (see clockSeenMoving), and the next heal is quick again.
    const healFailed = followsHeal && this.clockSeenMoving;
    if (healFailed) {
      this.healsWithoutEffect += 1;
      this.restartUnannounced = true;
      this.log(`check: frozen, ${outcome}; heal did not take: ${this.healsWithoutEffect} in a row`);
    }
    if (this.consecutiveHeals >= maxConsecutiveHeals) {
      // No more heals, but the sampling goes on, so the play tap's check never compares with a
      // sample older than this, and the next check that finds the clock moving resets the count.
      // Quiet, or a paused song on a dead output would log once a second.
      this.scheduleCheck(restartCheckDelayMs);
      return;
    }
    if (!healFailed) {
      this.log(`check: frozen, ${outcome}${followsHeal ? ', after a heal, on a clock never seen moving' : ''}`);
    }
    this.consecutiveHeals += 1;
    if (this.consecutiveHeals === maxConsecutiveHeals) {
      // Once per run of heals: the count only reaches the cap again after a moving clock or a hide
      // resets it, and the play tap's heals are not counted
      this.log(
        `cap reached: heal ${maxConsecutiveHeals} in a row; the watchdog makes no more heals until the clock ` +
          'moves or the page is hidden, and leaves the context to the play tap',
      );
    }
    this.heal('a return to the foreground').catch((error: unknown) => {
      console.error('Page audio context: healing a frozen clock failed', error);
    });
  }

  // Suspend, and only once that has landed resume: back to back, the resume would race the suspend.
  // Quick (the resume as soon as the suspends land), unless a heal has already been found not to
  // take since the page was last hidden or its clock seen moving: then the output is taken for dead,
  // and the suspend is held for heldSuspendMs first (see there). Whatever the outcome, or the
  // timeout should there be none, the clock is sampled and checked again, after a longer grace
  // counted from the resume (see restartCheckDelayMs), so a heal that did not take is retried at
  // once by that check (up to the cap) while the page stays visible, and a context left suspended by
  // a refused resume is resumed. The suspend and the resume each get browserCallTimeoutMs of their
  // own, and the other contexts' suspends otherSuspendTimeoutMs, after which the heal goes on
  // without them; the hold between them is the watchdog's own timer and is never cut short by a
  // timeout (one timeout over the whole chain let a suspend that landed late see its hold ended by
  // the next check). Every step of the heal's own chain checks that no newer heal has started since,
  // and that the watchdog has not let go of it (see the header): a heal let go of can land long
  // after.
  private heal(reason: string): Promise<void> {
    const context = this.context;
    this.healsStarted += 1;
    const number = this.healsStarted;
    const isLatest = () => this.healsStarted === number;
    let letGo = false; // one of its browser calls outlived browserCallTimeoutMs
    const isCurrent = () => isLatest() && !letGo;
    const holdMs = this.healsWithoutEffect > 0 ? heldSuspendMs : 0;
    const kind = holdMs > 0 ? `held ${holdMs} ms` : 'quick';
    if (this.warnedHeals < maxHealWarnings) {
      this.warnedHeals += 1;
      console.warn(
        `Page audio context: the clock is frozen at ${context.currentTime} s while running; ` +
          `suspending and resuming it (${kind}, ${reason}, issues #4098 and #4116)`,
      );
    }
    // Every other context that runs now goes down and comes back with this one: WebKit shares one
    // output unit among them, and it only stops once none of them runs (see the header). One that
    // does not run now is someone else's to start, and is left alone.
    const others = liveAudioContexts().filter((other) => other !== context && other.state === 'running');
    const withOthers = `with ${others.length} other context${others.length === 1 ? '' : 's'}`;
    this.log(`heal ${number} start (${kind}) for ${reason}, ${withOthers}`);
    this.suspendedByWatchdog = true;
    this.restartOwed = true;
    this.healing = true;
    const startedAt = performance.now();
    let suspendedAt: number | undefined;
    let resumingAt: number | undefined;
    // Rejects once a browser call of this heal outlives its deadline: the heal is let go of
    let letGoOf: (error: unknown) => void = () => undefined;
    const lettingGo = new Promise<never>((_resolve, reject) => {
      letGoOf = reject;
    });
    // `call` itself, for the heal's own chain to await whenever it settles, with the deadline on it
    // that lets go of the heal (see onDeadline)
    const waitAtMost = <T>(call: Promise<T>, what: string): Promise<T> =>
      onDeadline(call, () => {
        letGo = true;
        letGoOf(deadlineError(what));
      });
    const superseded = (step: string) =>
      this.log(isLatest() ? `heal ${number}: its ${step} settled after it was let go` : `heal ${number} superseded`);
    // A chain no longer current whose suspend has landed. With no heal under way (the newer one has
    // resumed the context and settled, or there is none, this one having been let go of), the
    // context is left suspended by this stale suspend, and nothing else resumes it before the next
    // tap, since every check bails on a context that is not running: so it is the watchdog's own
    // suspend to undo. A heal under way, quick or held, resumes the context itself, and with it
    // every other context in suspendedOthers. The other contexts this chain suspended are no longer
    // its to resume: the watchdog resumed them when it let go of this heal (in the `finally` below),
    // and one whose own suspend lands late is dealt with where it lands (see suspendOthers).
    const supersededAfterSuspend = () => {
      superseded('suspend');
      if (this.healing || context.state !== 'suspended') {
        return;
      }
      this.suspendedByWatchdog = true;
      this.restartOwed = true;
      this.scheduleCheck();
    };
    // The browser's own chain, which runs on whenever the browser settles each call, the watchdog
    // waiting on it or not
    const restart = (async () => {
      const suspending = waitAtMost(context.suspend(), "A heal's suspend");
      // Only this context's suspend gates the heal: another context's that is refused, or that has
      // not settled within otherSuspendTimeoutMs, fails nothing
      await Promise.all([suspending, this.suspendOthers(others, number)]);
      suspendedAt = performance.now();
      if (!isCurrent()) {
        supersededAfterSuspend();
        return;
      }
      if (holdMs > 0) {
        await sleep(holdMs);
        if (!isCurrent()) {
          supersededAfterSuspend();
          return;
        }
      }
      // A quick heal resumes in the same task as the suspends land, as it always has (one of another
      // context still pending holds it up to otherSuspendTimeoutMs)
      resumingAt = performance.now();
      this.holdingHeal = undefined;
      // A context created since the start, or one closed, changes what keeps the output unit
      // running: logged with the heal's end
      const othersAtResume = this.othersAtResume();
      // The others come back in the same task, unwaited: a resume made outside a gesture may never
      // settle (see browserCallTimeoutMs), and only this context's says whether the heal took
      this.resumeOthers([...this.suspendedOthers]);
      await waitAtMost(context.resume(), "A heal's resume");
      if (!isLatest()) {
        superseded('resume');
        return;
      }
      if (letGo) {
        // The browser released the resume after the watchdog let go of it, with no newer heal
        // started: the clock restarted all the same, so the listeners hear of it, once (the
        // watchdog's own resume of the context, made meanwhile, may have announced it already)
        superseded('resume');
        this.announceRestartOnce();
        return;
      }
      const suspendMs = Math.round(suspendedAt - startedAt);
      const hold = holdMs > 0 ? `, hold ${Math.round(resumingAt - suspendedAt)} ms` : '';
      const atResume = othersAtResume === others.length ? '' : `, ${othersAtResume} at its resume`;
      this.log(
        `heal ${number} end (${kind}): suspend ${suspendMs} ms${hold}, resume ${elapsedMs(resumingAt)} ms, ` +
          `${withOthers}${atResume}`,
      );
      this.nextCheckJudgesHeal = true;
      this.announceRestartOnce();
    })();
    const healing = Promise.race([restart, lettingGo])
      .catch((error: unknown) => {
        const step = suspendedAt === undefined ? 'suspend' : 'resume';
        this.log(
          `heal ${number} end (${kind}): failed in its ${step} after ${elapsedMs(startedAt)} ms: ${errorNameAndMessage(error)}`,
        );
        throw error;
      })
      .finally(() => {
        if (!isLatest()) {
          return;
        }
        this.healing = false;
        this.holdingHeal = undefined;
        // A heal that failed or was let go before its resume leaves the others suspended
        this.resumeOthers([...this.suspendedOthers]);
        this.scheduleCheck(restartCheckDelayMs);
      });
    if (holdMs > 0) {
      this.holdingHeal = healing;
    }
    return healing;
  }

  // Once per scheduled check while it is pending; a refusal, or a resume still pending after
  // browserCallTimeoutMs, waits for the next scheduled check (a return to the foreground) or for the
  // tap, whose own resume needs no help from here. Scheduling a check again at once would only
  // repeat the same resume outside a gesture.
  private resumeOwnSuspend() {
    if (this.resumingOwnSuspend) {
      return;
    }
    this.resumingOwnSuspend = true;
    this.log('resuming the context a heal left suspended');
    const resume = this.context.resume().then(() => this.announceRestartOnce());
    withDeadline(resume, 'Resuming the context a heal suspended')
      .catch((error: unknown) => {
        console.error('Page audio context: resuming the context a heal suspended failed', error);
      })
      .finally(() => {
        this.resumingOwnSuspend = false;
      });
  }

  // Suspends `others`, the app's other running contexts, for heal `number`, and resolves once every
  // suspend has settled or otherSuspendTimeoutMs has passed, whichever comes first; it never
  // rejects. Each joins suspendedOthers as its suspend lands while a heal is under way, so the heal
  // resumes it, and is resumed at once should it land with none under way (after the heal it was
  // for was let go of, say). One that is refused is logged and joins them the same way (a resume of
  // a context that runs does nothing), unless it has closed meanwhile, which needs nothing. One
  // still pending at the deadline is logged, and dealt with as it lands whenever it does.
  private suspendOthers(others: AudioContext[], number: number): Promise<void> {
    const pending = new Set(others.filter((other) => other.state !== 'closed'));
    const landed = (other: AudioContext) => {
      if (this.healing) {
        this.suspendedOthers.add(other);
      } else {
        this.resumeOthers([other]);
      }
    };
    const suspends = [...pending].map((other) =>
      other.suspend().then(
        () => {
          if (!pending.delete(other)) {
            this.log(`heal ${number}: another context's suspend landed after the heal went on without it`);
          }
          landed(other);
        },
        (error: unknown) => {
          pending.delete(other);
          if (other.state === 'closed') {
            return;
          }
          this.log(`heal ${number}: another context's suspend refused: ${errorNameAndMessage(error)}`);
          landed(other);
        },
      ),
    );
    return new Promise<void>((resolve) => {
      const deadline = setTimeout(() => {
        for (const _other of pending) {
          this.log(`heal ${number}: another context's suspend did not settle in ${otherSuspendTimeoutMs} ms`);
        }
        pending.clear();
        resolve();
      }, otherSuspendTimeoutMs);
      void Promise.all(suspends).then(() => {
        clearTimeout(deadline);
        resolve();
      });
    });
  }

  // Resumes `contexts`, other contexts a heal suspended, together, and waits on none of them; one
  // that refuses is logged and fails nothing, and a closed one needs nothing
  private resumeOthers(contexts: AudioContext[]) {
    for (const other of contexts) {
      this.suspendedOthers.delete(other);
      if (other.state === 'closed') {
        continue;
      }
      other.resume().catch((error: unknown) => {
        if (other.state !== 'closed') {
          this.log(`another context's resume refused: ${errorNameAndMessage(error)}`);
        }
      });
    }
  }

  // How many of the app's other contexts will run once a heal resumes: those it suspended, and any
  // other running then (one created during the hold, or one whose suspend never landed)
  private othersAtResume(): number {
    return liveAudioContexts().filter(
      (other) => other !== this.context && (other.state === 'running' || this.suspendedOthers.has(other)),
    ).length;
  }

  // Announces the restart of the clock a resume of a context the watchdog suspended has made,
  // unless it has been announced already: several resumes can settle for one suspend, and they are
  // one restart.
  private announceRestartOnce() {
    if (this.restartOwed) {
      this.restarted();
    }
  }

  // A restart owes no other announcement, so a heal that takes after one that did not is one restart
  // and the check that then finds the clock moving announces no other
  private restarted() {
    this.suspendedByWatchdog = false;
    this.restartOwed = false;
    this.restartUnannounced = false;
    for (const listener of this.restartListeners) {
      try {
        listener();
      } catch (error) {
        console.error('Page audio context: a clock restart listener failed', error);
      }
    }
  }

  private takeSample(): ClockSample {
    return { time: this.context.currentTime, at: performance.now() };
  }
}

const watchdogs = new WeakMap<AudioContext, AudioClockWatchdog>();

// Watches `context`'s clock from now on (see the header); the app does this once, for the page's
// context, and a test does it for a fake context with the browser's pieces injected
export function watchAudioClock(context: AudioContext, options: AudioClockWatchdogOptions = {}) {
  if (watchdogs.has(context)) {
    return;
  }
  watchdogs.set(context, new AudioClockWatchdog(context, options));
}

// Calls `listener` after every restart of `context`'s clock a heal made (see the header), whether
// the watchdog started it or a play tap did, and once when the clock is found moving again after a
// heal that did not take, until the returned function is called. Whoever maps another clock onto
// this one needs it: a restart the watchdog makes by itself goes through no player. The moment is
// exact for a heal's own restart, which is announced as its resume settles, and known only to within
// a check interval (restartCheckDelayMs) for an output that came back by itself, which is announced
// when the check that finds the clock moving runs. A context nothing watches (a test's fake) is
// never restarted, so nothing is called.
export function subscribeAudioClockRestarts(context: AudioContext, listener: () => void): () => void {
  return watchdogs.get(context)?.subscribeRestarts(listener) ?? (() => undefined);
}

let pageAudioContext: AudioContext | undefined;

export function getPageAudioContext(): AudioContext {
  if (!pageAudioContext) {
    pageAudioContext = createAudioContext({ latencyHint: 'interactive' });
    watchAudioClock(pageAudioContext);
  }
  return pageAudioContext;
}

// Starts the context for a tap that is about to play on it: call synchronously inside the tap,
// because the suspend or resume is called before anything is awaited and iOS wants the first resume
// inside the gesture. A context that is not running is resumed (an interrupted one included); a
// running one whose watched clock has not moved is healed with a suspend and a resume (see the
// header); any other running one is resumed too, which does nothing, costs nothing, and is what
// Learn has always asked, and its clock is checked again a moment later, healed then should it not
// have moved. Resolves true when the context had to be started or restarted, so a caller can tell
// that the output timestamps from before it are stale, and false when it was running already (a
// later heal reaches subscribeAudioClockRestarts() instead); rejects when the browser refuses, or
// when a suspend or a resume of a heal this tap started or waits on has not settled within
// browserCallTimeoutMs. A context nothing watches (a test's fake) is only ever resumed.
export function resumePageAudioContext(context: AudioContext): Promise<boolean> {
  const watchdog = watchdogs.get(context);
  if (watchdog) {
    return watchdog.resumeForTap();
  }
  return resumeReportingStart(context);
}

// The plain resume, a watched context's and an unwatched one's: resolves true when `context` was
// not running before it
function resumeReportingStart(context: AudioContext): Promise<boolean> {
  const wasRunning = context.state === 'running';
  return context.resume().then(() => !wasRunning);
}
